import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./CotdSessionPage.css";
import clsx from "clsx";
import { isLocalhost } from "@/lib/debug/debug";
import { useKeyboard } from "@/lib/hooks/useKeyboard";
import {
  CardName,
  getRandomTarotCard,
  preloadCardImage,
} from "@/lib/constants/tarot";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ReadingCategory } from "@/lib/types/i18n-types";
import { cardRevealWorkflow } from "@/lib/utils/animation/animationWorkflows";
import {
  getCategoryData,
  getCategoryGuidanceKey,
} from "@/lib/utils/categories";
import CardImage from "../CardImage";
import CardImageModal from "../CardImageModal";
import { Button } from "../ui/button";

import Animation from "../Animation";

function CotdSessionPage() {
  const [searchParams] = useSearchParams();
  const [dailyCard, setDailyCard] = useState<CardName | null>(null);
  const [cardRevealed, setCardRevealed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCardInfo, setShowCardInfo] = useState(false);
  const { t, tCard, tReading } = useLanguage();
  const category = searchParams.get("category") || "general";
  const categoryData = getCategoryData(category, t);

  // Load card on component mount
  useEffect(() => {
    const loadInitialCard = async () => {
      // Hard code "The Fool" for testing
      // const initialCard = "The Fool" as CardName;
      const initialCard = getRandomTarotCard();
      try {
        await preloadCardImage(initialCard);
      } catch (error) {
        console.warn("Failed to preload initial card image:", error);
      }
      setDailyCard(initialCard);
    };

    loadInitialCard();
  }, []); // Empty dependency array to run only on mount

  const getCardReadingContent = () => {
    if (!dailyCard) return null;

    const readingCategory =
      category === "general" ? "love" : (category as ReadingCategory);

    return tReading(readingCategory, dailyCard);
  };

  const handleImageClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = (open: boolean) => {
    setIsModalOpen(open);
  };

  const resetSession = () => {
    // setDailyCard(null);
    setCardRevealed(false);
    setShowCardInfo(false);
  };

  useKeyboard("r", resetSession, {
    enabled: !!(showCardInfo || dailyCard),
    preventDefault: true,
  });

  return (
    <div className="cotd-session-container flex-1 flex flex-col p-4">
      <div className="cotd-session-content text-center w-full max-w-sm sm:max-w-lg lg:max-w-2xl mx-auto flex-1 flex flex-col">
        <div
          className={clsx(
            `card-display-area bg-secondary border-2 border-secodary relative`,
            !showCardInfo
              ? "p-6 sm:p-8 flex-1 flex items-center justify-center"
              : "p-4 sm:p-6 flex-1 flex flex-col",
          )}
        >
          <div
            className={`category-indicator absolute top-2 right-2 inline-block px-2 py-1 rounded-full ${categoryData.bgColor} ${categoryData.borderColor} border z-10`}
          >
            <p className={`text-xs ${categoryData.textColor}`}>
              <span className="category-name font-medium">
                {categoryData.name}
              </span>
            </p>
          </div>
          {!showCardInfo ? (
            /* Animation Container */
            <div className="animation-container">
              {!showCardInfo && (
                <div className="card-prompt-section text-center relative">
                  <Animation
                    simple={cardRevealWorkflow(() => {
                      console.log("Card revealed during animation at 90°");
                      // Reveal the already-loaded card at 90° point
                      setCardRevealed(true);
                    }, 2000)}
                    onComplete={() => {
                      console.log("Card reveal animation completed");
                      // Show card info after reveal animation
                      setShowCardInfo(true);
                    }}
                    trigger="click"
                  >
                    <CardImage
                      cardName={cardRevealed ? dailyCard : null}
                      className="w-48 h-80 sm:w-56 sm:h-92 rounded-lg"
                      alt={dailyCard || "Tarot card"}
                    />
                  </Animation>
                </div>
              )}
            </div>
          ) : (
            /* Final State: Card Info Layout */
            showCardInfo &&
            dailyCard && (
              <div className={"card-layout-fade-in"}>
                {/* <div> */}
                {/* Row 1: Card Image + Name/Keywords */}
                <div className="card-header-row flex gap-4 sm:gap-6">
                  {/* Left: Card Image */}
                  <div className="card-image-section flex-shrink-0 relative">
                    <Animation
                      steps={[
                        {
                          id: "start-at-center",
                          name: "Start at center position",
                          duration: 0,
                          transform: {
                            translateX: "calc(100%)",
                            translateY: "calc(100%)",
                            scale: 1.8,
                          },
                        },
                        {
                          id: "move-to-final",
                          name: "Move to final position",
                          duration: 900,
                          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: {
                            translateX: "0",
                            translateY: "0",
                            scale: 1.0,
                          },
                        },
                      ]}
                      trigger="mount"
                    >
                      <CardImage
                        cardName={dailyCard}
                        className="w-24 h-40 sm:w-32 sm:h-53 lg:w-36 lg:h-60 rounded-lg"
                        alt={dailyCard || "Tarot card back"}
                        clickable={true}
                        onClick={handleImageClick}
                      />
                    </Animation>
                  </div>

                  {/* Right: Card Name + Keywords (vertical stack) */}
                  <div className="card-name-section flex-1 flex flex-col justify-center space-y-1 sm:space-y-2">
                    <h3 className="card-name text-2xl font-medium">
                      {dailyCard}
                    </h3>
                    <p className="card-keywords text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {tCard(dailyCard)}
                    </p>
                  </div>
                </div>

                {/* Row 2: Reading Result */}
                <div className="card-reading-section mt-2 border-t border-gray-200 dark:border-gray-700 pt-4 sm:pt-6">
                  {(() => {
                    const cardReading = getCardReadingContent();
                    if (cardReading) {
                      return (
                        <>
                          <h4 className="reading-title text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">
                            {cardReading.title}
                          </h4>
                          <p className="card-guidance text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                            {cardReading.reading}
                          </p>
                        </>
                      );
                    }
                    // Fallback to generic guidance if no specific reading exists
                    const guidanceKey = getCategoryGuidanceKey(category);
                    return (
                      <p className="card-guidance text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                        {t(guidanceKey as any)}
                      </p>
                    );
                  })()}
                </div>
              </div>
            )
          )}
        </div>

        <div className="header-section mb-6 relative">
          {(showCardInfo || dailyCard) && isLocalhost() && (
            <Button
              variant="outline"
              size="sm"
              onClick={resetSession}
              className="absolute top-0 right-0 text-xs"
            >
              Reset
            </Button>
          )}
        </div>

        {/* Image Preview Modal */}
        <CardImageModal
          cardName={dailyCard}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    </div>
  );
}

export default CotdSessionPage;
